import {
  MAX_COLUMN_WIDTH_EM,
  MAX_FONT_SIZE_PT,
  MAX_LOGO_HEIGHT_MM,
  MAX_PAGE_MARGIN_MM,
  MAX_SPACER_HEIGHT_MM,
  REPORT_GRID_COLUMNS,
} from '../constants';
import { ReportBlock, ReportLeafBlock, ReportTemplate } from '../types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isString = (value: unknown): value is string => typeof value === 'string';
const isNonEmptyString = (value: unknown): value is string =>
  isString(value) && value.trim().length > 0;
const isOptionalString = (value: unknown): boolean =>
  value === undefined || isString(value);
const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);
const isOptionalBoolean = (value: unknown): boolean =>
  value === undefined || typeof value === 'boolean';
const isOneOf = (value: unknown, allowed: readonly string[]): boolean =>
  isString(value) && allowed.includes(value);
const isOptionalOneOf = (value: unknown, allowed: readonly string[]): boolean =>
  value === undefined || isOneOf(value, allowed);

const ALIGNMENTS = ['left', 'center', 'right'];
const META_FIELDS = [
  'printedAt',
  'filters',
  'rowCount',
  'reportTitle',
  'filterValue',
  'datasetLabel',
];
const OPERATIONS = ['sum', 'count', 'avg', 'min', 'max'];

const isReportDataSource = (value: unknown): boolean =>
  value === 'master' ||
  (isString(value) &&
    value.startsWith('subset:') &&
    value.slice('subset:'.length).trim().length > 0);

const isTextStyle = (value: unknown): boolean =>
  isRecord(value) &&
  (value.fontSizePt === undefined ||
    (isFiniteNumber(value.fontSizePt) &&
      value.fontSizePt > 0 &&
      value.fontSizePt <= MAX_FONT_SIZE_PT)) &&
  isOptionalBoolean(value.bold) &&
  isOptionalBoolean(value.italic) &&
  isOptionalBoolean(value.underline) &&
  isOptionalString(value.color) &&
  isOptionalOneOf(value.fontFamily, ['sans', 'serif', 'mono']);

const hasOptionalStyle = (value: Record<string, unknown>): boolean =>
  value.style === undefined || isTextStyle(value.style);

const isFieldRef = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyString(value.item) &&
  (value.source === undefined || isReportDataSource(value.source)) &&
  isOptionalString(value.label) &&
  isOptionalString(value.format) &&
  hasOptionalStyle(value);

export const isReportLeafBlock = (value: unknown): value is ReportLeafBlock => {
  if (!isRecord(value) || !isString(value.kind)) {
    return false;
  }

  switch (value.kind) {
    case 'text':
      return (
        isString(value.text) &&
        isOptionalOneOf(value.align, ALIGNMENTS) &&
        hasOptionalStyle(value)
      );
    case 'fields':
      return (
        (value.source === undefined || isReportDataSource(value.source)) &&
        (value.columns === undefined ||
          (Number.isInteger(value.columns) &&
            Number(value.columns) > 0 &&
            Number(value.columns) <= REPORT_GRID_COLUMNS)) &&
        Array.isArray(value.fields) &&
        value.fields.length > 0 &&
        value.fields.every(isFieldRef)
      );
    case 'aggregate':
      return (
        isString(value.label) &&
        isOneOf(value.operation, OPERATIONS) &&
        (value.operation === 'count' || isNonEmptyString(value.item)) &&
        isReportDataSource(value.source) &&
        isOptionalString(value.format) &&
        hasOptionalStyle(value)
      );
    case 'taxBreakdown':
      return (
        isString(value.label) &&
        isReportDataSource(value.source) &&
        isNonEmptyString(value.groupBy) &&
        isNonEmptyString(value.net) &&
        isNonEmptyString(value.gross) &&
        value.taxFrom === 'difference' &&
        hasOptionalStyle(value)
      );
    case 'logo':
      return (
        (value.assetId === undefined || isNonEmptyString(value.assetId)) &&
        (value.src === undefined || isNonEmptyString(value.src)) &&
        (value.heightMm === undefined ||
          (isFiniteNumber(value.heightMm) &&
            value.heightMm > 0 &&
            value.heightMm <= MAX_LOGO_HEIGHT_MM)) &&
        isOptionalOneOf(value.align, ALIGNMENTS)
      );
    case 'meta':
      return (
        isOneOf(value.field, META_FIELDS) &&
        isOptionalString(value.filterItem) &&
        (value.filterSource === undefined ||
          isReportDataSource(value.filterSource)) &&
        isOptionalString(value.label) &&
        isOptionalOneOf(value.align, ALIGNMENTS) &&
        hasOptionalStyle(value)
      );
    case 'spacer':
      return (
        value.heightMm === undefined ||
        (isFiniteNumber(value.heightMm) &&
          value.heightMm >= 0 &&
          value.heightMm <= MAX_SPACER_HEIGHT_MM)
      );
    case 'line':
      return true;
    default:
      return false;
  }
};

const isRowCell = (value: unknown): boolean =>
  isRecord(value) &&
  (value.span === undefined ||
    (Number.isInteger(value.span) &&
      Number(value.span) >= 1 &&
      Number(value.span) <= REPORT_GRID_COLUMNS)) &&
  isOptionalOneOf(value.align, ALIGNMENTS) &&
  Array.isArray(value.blocks) &&
  value.blocks.length > 0 &&
  value.blocks.every(isReportLeafBlock);

const isReportBlock = (value: unknown): value is ReportBlock =>
  isReportLeafBlock(value) ||
  (isRecord(value) &&
    value.kind === 'row' &&
    Array.isArray(value.cells) &&
    value.cells.length > 0 &&
    value.cells.every(isRowCell));

const isOptionalSection = (value: unknown): boolean =>
  value === undefined ||
  (isRecord(value) &&
    Array.isArray(value.blocks) &&
    value.blocks.every(isReportBlock) &&
    isOptionalBoolean(value.border));

const isOptionalHeaderSection = (value: unknown): boolean =>
  isOptionalSection(value) &&
  (value === undefined ||
    (isRecord(value) &&
      (value.source === undefined || isReportDataSource(value.source))));

const isColumn = (value: unknown): boolean =>
  isRecord(value) &&
  isNonEmptyString(value.item) &&
  (value.width === undefined ||
    (isFiniteNumber(value.width) &&
      value.width > 0 &&
      value.width <= MAX_COLUMN_WIDTH_EM)) &&
  isOptionalOneOf(value.align, ALIGNMENTS) &&
  isOptionalString(value.label) &&
  isOptionalString(value.format) &&
  hasOptionalStyle(value);

const isItemsDataRow = (
  value: unknown
): value is Record<string, unknown> =>
  isRecord(value) &&
  isReportDataSource(value.source) &&
  Array.isArray(value.columns) &&
  value.columns.every(isColumn) &&
  isOptionalBoolean(value.repeatHeaderOnEachPage) &&
  (value.gridLines === undefined ||
    (isRecord(value.gridLines) &&
      isOptionalBoolean(value.gridLines.vertical) &&
      isOptionalBoolean(value.gridLines.horizontal)));

/** Full runtime gate before an API layout reaches React rendering. */
export const isReportTemplateLayout = (
  value: unknown
): value is ReportTemplate => {
  if (!isRecord(value) || !isRecord(value.page) || !isRecord(value.sections)) {
    return false;
  }

  const { page, sections } = value;
  const items = sections.items;
  return (
    value.version === 1 &&
    isNonEmptyString(value.templateId) &&
    isNonEmptyString(value.datasetId) &&
    isString(value.label) &&
    isOneOf(page.size, ['A4', 'A5', 'Letter']) &&
    isOneOf(page.orientation, ['portrait', 'landscape']) &&
    isFiniteNumber(page.marginMm) &&
    page.marginMm >= 0 &&
    page.marginMm <= MAX_PAGE_MARGIN_MM &&
    typeof page.border === 'boolean' &&
    isItemsDataRow(items) &&
    (items.dataRows === undefined ||
      (Array.isArray(items.dataRows) &&
        items.dataRows.length > 0 &&
        items.dataRows.every(isItemsDataRow))) &&
    isOptionalHeaderSection(sections.header) &&
    isOptionalSection(sections.footer)
  );
};
